<template>
  <p-form
    ref="form"
    @submit="onSubmit"
  >
    <div class="c-row c-horizontal-center">
      <div class="c-col-2 sm:c-col-6 md:c-col-12 lg:c-col-10">
        <!-- Título "Dados Pessoais" e subtítulo -->
        <div class="c-row ui-mb-4">
          <div class="c-col-full">
            <!-- Título -->
            <div class="c-text-h3">
              Dados pessoais
            </div>

            <!-- Subtítulo -->
            <div class="c-text-b2 c-text-gray">
              *Preenchimento obrigatório
            </div>
          </div>
        </div>

        <!-- Nome completo -->
        <div class="c-row ui-mb-4">
          <validation-provider
            class="c-col-full sm:c-col ui-mb-4 sm:ui-mb-0"
            rules="required|full_name"
            name="fullName"
            v-slot="{ errors }"
          >
            <p-input
              data-cy="nameField"
              key="nameField"
              label="Nome completo*"
              name="name"
              v-model.trim="user.name"
              :error-message="errors[0]"
              :mask="fullNameRegex"
            />
          </validation-provider>

          <!-- E-mail -->
          <div class="c-col-full sm:c-col">
            <p-input
              data-cy="emailField"
              key="emailField"
              label="Email*"
              name="email"
              v-model.trim="user.email"
              readonly
              icon="lock"
              disabled
            />
          </div>
        </div>

        <!-- CPF e Telefone -->
        <div class="c-row ui-mb-4">
          <!-- CPF -->
          <validation-provider
            class="c-col-full sm:c-col"
            :rules="allowEditCpf ? 'required|cpf' : ''"
            v-slot="{ errors }"
            name="CPF"
            vid="cpf"
          >
            <p-input
              data-cy="cpfField"
              key="cpfField"
              label="CPF*"
              name="cpf"
              v-model="user.cpf"
              :error-message="errors[0]"
              mask="000.000.000-00"
              hint="Somente números"
              :icon="allowEditCpf ? undefined : 'lock'"
              icon-info="info"
              :disabled="!allowEditCpf"
            />
          </validation-provider>

          <!-- Telefone -->
          <validation-provider
            class="c-col-full sm:c-col"
            rules="required|length:11"
            name="Celular"
            v-slot="{ errors }"
            :custom-messages="{ length: 'Celular inválido' }"
          >
            <p-input
              data-cy="phoneField"
              key="phoneField"
              label="Celular*"
              name="phone"
              v-model="user.phone"
              hint="DDD + nº do celular"
              icon-info="info"
              :error-message="errors[0]"
              mask="(00) 0 0000-0000"
            />
          </validation-provider>

          <!-- Data de nascimento -->
          <validation-provider
            class="c-col-full sm:c-col"
            :rules="{ required: true, after: dateMinusYear(120), before: dateMinusYear(0) }"
            name="Data de nascimento"
            v-slot="{ errors }"
          >
            <p-input
              type="date"
              data-cy="birthdayField"
              key="birthdayField"
              label="Data de nascimento*"
              name="birthday"
              v-model="user.birthday"
              hint="Data de nascimento"
              icon-info="info"
              icon="calendar"
              :error-message="errors[0]"
            />
          </validation-provider>
        </div>

        <!-- MFA et al -->
        <div
          class="c-row ui-mb-4"
        >
          <!-- MFA -->
          <div class="c-col c-d-flex">
            <c-toggle
              name="require_mfa"
              v-model="user.mfa_required"
              class="ui-mr-4"
              hint="Ative para exigir autenticação em duas etapas"
            />
            <span class="c-text-b2 c-text-gray-300">
              Autenticação em duas etapas
            </span>
          </div>
        </div>

        <separator />
      </div>
    </div>

    <!-- Botão Alterar e Texto Cancelar -->
    <div class="c-row c-horizontal-center">
      <!-- Botão Alterar -->
      <div class="c-col-full sm:c-col-1/4 lg:c-col-2">
        <c-button
          data-cy="updateSubmitButton"
          type="button"
          @click="onSubmit"
          :loading="isSubmitting"
          class="ui-w-full ui-mb-4 sm:ui-mb-0"
        >
          Salvar alteração
        </c-button>
      </div>

      <!-- Botão Voltar -->
      <div class="c-col-full sm:c-col-1/4 lg:c-col-2">
        <router-link :to="{ name: 'meus-dados' }">
          <c-button
            outline
            :disabled="isSubmitting"
            class="ui-w-full"
          >
            Voltar sem salvar
          </c-button>
        </router-link>
      </div>
    </div>
  </p-form>
</template>

<script>
import { mapGetters } from 'vuex';
import { CButton, CToggle } from '@estrategiahq/coruja-web-ui';
import { cpfAlreadyExistsError } from '@/utils/errors';
import { User } from '@/models/user';
import PForm from '@/components/p-form';
import PInput from '@/components/p-input';
import Separator from '@/components/separator';
import { UserStoreKeys } from '@/store/modules/user';
import { ValidationProvider } from 'vee-validate';
import { dateMinusYear } from '@/utils/date';
import { FULL_NAME_REGEX } from '@/utils/regex';
import axios from 'axios';

export default {
  components: {
    CButton,
    PForm,
    CToggle,
    PInput,
    Separator,
    ValidationProvider,
  },
  data: () => ({
    user: new User(),
    allowEditCpf: true,
    fullNameRegex: FULL_NAME_REGEX,
  }),
  async mounted() {
    await this.$store.dispatch(UserStoreKeys.ACTION_FETCH_LOGGED_USER);
    this.user = { ...this.loggedUser };
    this.allowEditCpf = !this.user.cpf;
  },
  computed: {
    ...mapGetters({
      loggedUser: UserStoreKeys.GET_LOGGED_USER,
      isSubmitting: UserStoreKeys.GET_REQUEST_LOADING,
    }),
  },
  methods: {
    dateMinusYear(year) {
      return dateMinusYear(year);
    },
    async onSubmit() {
      const isValid = await this.$refs.form.validate();
      if (!isValid) {
        this.$toast.show({
          type: 'warning',
          text: 'Preencha os dados obrigatórios',
        });
        return;
      }
      try {
        await this.$store.dispatch(UserStoreKeys.ACTION_UPDATE_LOGGED_USER, this.user);
        if (this.$isPreview('fase2')) {
          // Se estiver em preview busca novamente os dados do user para passar pelo mock
          await this.$store.dispatch(UserStoreKeys.ACTION_FETCH_LOGGED_USER);
        }
        this.$router.push({ name: 'meus-dados' });
      } catch (error) {
        if (error === cpfAlreadyExistsError) {
          const email = error.meta.email;
          this.$refs.form.setErrors({
            cpf: [`CPF já cadastrado para o email ${email}`],
          });
          this.$toast.show({
            type: 'error',
            text: 'Este CPF já está associado a uma conta existente.',
          });
          return;
        }

        if (!axios.isAxiosError(error)) {
          throw error;
        }
        if (!Array.isArray(error.response.data.errors)) {
          throw error;
        }

        error.response.data.errors.forEach((error) => {
          if (error.includes('full_name')) {
            this.$refs.form.setErrors({
              fullName: ['Nome informado é inválido'],
            });
          }
        });

        throw new Error('Ocorreu um erro ao atualizar os dados');
      }
    },
  },
};
</script>
